import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useMealMate } from "../../context/MealMateContext";
import SideNavAccount from "../../components/SideNav/SideNavAccount";
import { Form, Input, Button, message, Select, Spin } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { api, endpoints } from "../../utils/axiosInstance";

const { Option } = Select;

const ProfilePage = () => {
  const { setUser } = useMealMate();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [isFetching, setIsFetching] = useState(true);

  const fetchUserProfile = useCallback(async () => {
    try {
      setIsFetching(true);
      const response = await api.get(endpoints.user.profile);

      if (response && response.data) {
        setProfileData(response.data);
        setImageUrl(response.data.profile_picture);
        form.setFieldsValue({
          full_name: response.data.full_name,
          email: response.data.email,
          phone: response.data.phone,
          gender: response.data.gender,
          height: response.data.height,
          weight: response.data.weight,
          date_of_birth: response.data.date_of_birth
            ? new Date(response.data.date_of_birth).toISOString().split("T")[0]
            : undefined,
        });
      }
    } catch (error) {
      if (error.response?.status === 401) {
        message.error("Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại");
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("userInfo");
        navigate("/login");
      } else {
        message.error(
          "Không thể tải thông tin profile: " +
            (error.message || "Đã có lỗi xảy ra")
        );
      }
    } finally {
      setIsFetching(false);
    }
  }, [navigate, form]);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      message.error("Vui lòng đăng nhập để tiếp tục");
      navigate("/login");
      return;
    }

    fetchUserProfile();
  }, [navigate, fetchUserProfile]);

  useEffect(() => {
    if (profileData) {
      form.setFieldsValue({
        full_name: profileData.full_name,
        email: profileData.email,
        phone: profileData.phone,
        gender: profileData.gender,
        height: profileData.height,
        weight: profileData.weight,
        date_of_birth: profileData.date_of_birth
          ? new Date(profileData.date_of_birth).toISOString().split("T")[0]
          : undefined,
      });
    }
  }, [profileData, form]);

  if (!profileData && isFetching) {
    return (
      <PageContainer>
        <SideNavAccount />
        <LoadingContainer>
          <Spin size="large" tip="Đang tải thông tin..." />
        </LoadingContainer>
      </PageContainer>
    );
  }

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const updatedData = {
        full_name: values.full_name?.trim(),
        phone: values.phone?.trim(),
        gender: values.gender,
        height: values.height ? Number(values.height) : null,
        weight: values.weight ? Number(values.weight) : null,
        date_of_birth: values.date_of_birth || null,
      };

      const response = await api.patch(
        endpoints.user.updateProfile,
        updatedData
      );

      if (response) {
        const updatedProfile = {
          ...profileData,
          ...updatedData,
        };

        setProfileData(updatedProfile);
        setUser(updatedProfile);

        form.setFieldsValue({
          ...updatedProfile,
          date_of_birth: updatedProfile.date_of_birth
            ? new Date(updatedProfile.date_of_birth).toISOString().split("T")[0]
            : undefined,
        });

        setIsEditing(false);
        message.success("Cập nhật thông tin thành công!");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        message.error("Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại");
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("userInfo");
        navigate("/login");
      } else {
        message.error(
          "Không thể cập nhật thông tin: " +
            (error.response?.data?.message ||
              error.message ||
              "Đã có lỗi xảy ra")
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (profileData) {
      form.setFieldsValue({
        full_name: profileData.full_name,
        email: profileData.email,
        phone: profileData.phone,
        gender: profileData.gender,
        height: profileData.height,
        weight: profileData.weight,
        date_of_birth: profileData.date_of_birth
          ? new Date(profileData.date_of_birth).toISOString().split("T")[0]
          : undefined,
      });
    }
    setIsEditing(false);
  };

  return (
    <PageContainer>
      <SideNavAccount />
      <MainContent>
        <ContentHeader>
          <h1>Thông tin cá nhân</h1>
          <p>Quản lý thông tin và tùy chọn cá nhân của bạn</p>
        </ContentHeader>

        <ProfileSection>
          <ProfileImageSection>
            <ProfileImageWrapper>
              {imageUrl ? (
                <ProfileImage src={imageUrl} alt="Ảnh đại diện" />
              ) : (
                <UserOutlined style={{ fontSize: "40px", color: "#999" }} />
              )}
            </ProfileImageWrapper>
          </ProfileImageSection>

          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <FormGrid>
              <FormSection>
                <SectionTitle>Thông tin cơ bản</SectionTitle>
                <Form.Item
                  name="full_name"
                  label="Họ và tên"
                  rules={[{ required: true, message: "Vui lòng nhập họ tên" }]}
                >
                  <Input disabled={!isEditing} />
                </Form.Item>
                <Form.Item name="email" label="Email">
                  <Input disabled={true} />
                </Form.Item>
                <Form.Item
                  name="phone"
                  label="Số điện thoại"
                  rules={[
                    {
                      pattern: /^\d{10}$/,
                      message: "Số điện thoại phải có 10 chữ số",
                    },
                  ]}
                >
                  <Input disabled={!isEditing} />
                </Form.Item>
                <Form.Item name="gender" label="Giới tính">
                  <Select disabled={!isEditing}>
                    <Option value="male">Nam</Option>
                    <Option value="female">Nữ</Option>
                    <Option value="other">Khác</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="date_of_birth"
                  label="Ngày sinh"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve(); // Cho phép bỏ trống
                        const date = new Date(value);
                        const today = new Date();
                        if (isNaN(date.getTime())) {
                          return Promise.reject("Ngày sinh không hợp lệ");
                        }
                        if (date > today) {
                          return Promise.reject(
                            "Ngày sinh không được trong tương lai"
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  tooltip="Nhập ngày sinh của bạn để tính toán mục tiêu dinh dưỡng"
                >
                  <Input
                    type="date"
                    disabled={!isEditing}
                    placeholder="Chọn ngày sinh"
                  />
                </Form.Item>
              </FormSection>

              <FormSection>
                <SectionTitle>Thông tin sức khỏe</SectionTitle>
                <p style={{ color: "#666", marginBottom: "20px" }}>
                  Các thông tin này không bắt buộc nhưng sẽ giúp chúng tôi gợi ý
                  các công thức phù hợp hơn với bạn
                </p>
                <Form.Item
                  name="height"
                  label="Chiều cao (cm)"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve(); // Cho phép bỏ trống
                        const number = Number(value);
                        if (isNaN(number)) {
                          return Promise.reject("Chiều cao phải là một số");
                        }
                        if (number <= 0 || number > 300) {
                          return Promise.reject(
                            "Chiều cao phải trong khoảng 0 đến 300 cm"
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  tooltip="Nhập chiều cao của bạn để nhận gợi ý về khẩu phần ăn phù hợp"
                >
                  <Input
                    type="number"
                    disabled={!isEditing}
                    placeholder="Ví dụ: 170"
                  />
                </Form.Item>
                <Form.Item
                  name="weight"
                  label="Cân nặng (kg)"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve(); // Cho phép bỏ trống
                        const number = Number(value);
                        if (isNaN(number)) {
                          return Promise.reject("Cân nặng phải là một số");
                        }
                        if (number <= 0 || number > 300) {
                          return Promise.reject(
                            "Cân nặng phải trong khoảng 0 đến 300 kg"
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  tooltip="Nhập cân nặng của bạn để nhận gợi ý về lượng calories phù hợp"
                >
                  <Input
                    type="number"
                    disabled={!isEditing}
                    placeholder="Ví dụ: 60"
                  />
                </Form.Item>
              </FormSection>
            </FormGrid>

            <ButtonGroup>
              {!isEditing ? (
                <EditButton onClick={() => setIsEditing(true)}>
                  Chỉnh sửa
                </EditButton>
              ) : (
                <>
                  <SaveButton htmlType="submit" loading={loading}>
                    Lưu thay đổi
                  </SaveButton>
                  <CancelButton onClick={handleCancel}>Hủy</CancelButton>
                </>
              )}
            </ButtonGroup>
          </Form>
        </ProfileSection>
      </MainContent>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #faf7f5;
  padding: 2rem;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1rem;
  }
`;

const MainContent = styled.main`
  flex: 1;
  max-width: 1000px;
`;

const ContentHeader = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: 2rem;
    color: #333;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 1.1rem;
  }
`;

const ProfileSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const ProfileImageSection = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
`;

const ProfileImageWrapper = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 75px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  .ant-form-item {
    margin-bottom: 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f0f0f0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: flex-end;
`;

const StyledButton = styled(Button)`
  height: 40px;
  padding: 0 2rem;
  font-size: 1rem;
  border-radius: 20px;
`;

const EditButton = styled(StyledButton)`
  background: #ff4b4b;
  color: white;
  border: none;

  &:hover {
    background: #ff3333;
    color: white;
  }
`;

const SaveButton = styled(StyledButton)`
  background: #ff4b4b;
  color: white;
  border: none;

  &:hover {
    background: #ff3333;
    color: white;
  }
`;

const CancelButton = styled(StyledButton)`
  border: 2px solid #ff4b4b;
  color: #ff4b4b;
  background: white;

  &:hover {
    color: #ff3333;
    border-color: #ff3333;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

export default ProfilePage;
